import React, { useMemo, useState } from "react";
import { updateLeague } from "../../api/leagues";
import {
	toastError,
	toastInfo,
} from "../../helpers/notificationHelpers";
import {
	formatDateOnly,
	formatDateTime,
	toApiDate,
} from "./leagueUtils";

function EditLeague({ league, onSave }) {
	const initialLeague = useMemo(
		() => ({
			...league,
			countryId: league.countryId ?? "",
			championTeamId: league.championTeamId ?? "",
			subChampionTeamId: league.subChampionTeamId ?? "",
			startDate: formatDateOnly(league.startDate),
			endDate: formatDateOnly(league.endDate),
		}),
		[league]
	);
	const [currentLeague, setCurrentLeague] = useState(initialLeague);
	const [isSaving, setIsSaving] = useState(false);

	const handleChange = (event) => {
		const { checked, name, type, value } = event.target;

		setCurrentLeague((previousLeague) => ({
			...previousLeague,
			[name]: type === "checkbox" ? checked : value,
		}));
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		setIsSaving(true);

		const payload = {
			leagueIdentifier: league.uniqueIdentifier,
			name: currentLeague.name,
			type: currentLeague.type,
			countryId:
				currentLeague.countryId === "" ? null : Number(currentLeague.countryId),
			logoUrl: currentLeague.logoUrl,
			externalId: currentLeague.externalId,
			season: currentLeague.season,
			startDate: toApiDate(currentLeague.startDate),
			endDate: toApiDate(currentLeague.endDate),
			importFixtures: currentLeague.importFixtures,
			importFixtureResults: currentLeague.importFixtureResults,
			importFixtureEvents: currentLeague.importFixtureEvents,
			championTeamId:
				currentLeague.championTeamId === ""
					? null
					: Number(currentLeague.championTeamId),
			subChampionTeamId:
				currentLeague.subChampionTeamId === ""
					? null
					: Number(currentLeague.subChampionTeamId),
		};

		try {
			const response = await updateLeague(league.uniqueIdentifier, payload);

			if (!response || response.status < 200 || response.status >= 300) {
				toastError("Error actualizando la liga.");
				setIsSaving(false);
				return;
			}

			toastInfo("Liga actualizada correctamente.");
			if (onSave) {
				onSave(payload);
			}
		} catch (error) {
			toastError("Error actualizando la liga.");
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<div className='container mt-4'>
			<form onSubmit={handleSubmit}>
				<div className='row g-3'>
					<div className='col-12'>
						<label className='form-label' htmlFor='uniqueIdentifier'>
							Identificador
						</label>
						<input
							className='form-control'
							disabled
							id='uniqueIdentifier'
							readOnly
							type='text'
							value={currentLeague.uniqueIdentifier || ""}
						/>
					</div>
					<div className='col-12 col-md-6'>
						<label className='form-label' htmlFor='name'>
							Nombre
						</label>
						<input
							className='form-control'
							id='name'
							name='name'
							onChange={handleChange}
							type='text'
							value={currentLeague.name || ""}
						/>
					</div>
					<div className='col-12 col-md-6'>
						<label className='form-label' htmlFor='type'>
							Tipo
						</label>
						<input
							className='form-control'
							id='type'
							name='type'
							onChange={handleChange}
							type='text'
							value={currentLeague.type || ""}
						/>
					</div>
					<div className='col-12 col-md-6'>
						<label className='form-label' htmlFor='countryId'>
							CountryId
						</label>
						<input
							className='form-control'
							id='countryId'
							name='countryId'
							onChange={handleChange}
							type='number'
							value={currentLeague.countryId}
						/>
					</div>
					<div className='col-12 col-md-6'>
						<label className='form-label' htmlFor='externalId'>
							ExternalId
						</label>
						<input
							className='form-control'
							id='externalId'
							name='externalId'
							onChange={handleChange}
							type='text'
							value={currentLeague.externalId || ""}
						/>
					</div>
					<div className='col-12'>
						<label className='form-label' htmlFor='logoUrl'>
							LogoUrl
						</label>
						<input
							className='form-control'
							id='logoUrl'
							name='logoUrl'
							onChange={handleChange}
							type='text'
							value={currentLeague.logoUrl || ""}
						/>
					</div>
					<div className='col-12 col-md-4'>
						<label className='form-label' htmlFor='season'>
							Season
						</label>
						<input
							className='form-control'
							id='season'
							name='season'
							onChange={handleChange}
							type='text'
							value={currentLeague.season || ""}
						/>
					</div>
					<div className='col-12 col-md-4'>
						<label className='form-label' htmlFor='startDate'>
							Fecha inicio
						</label>
						<input
							className='form-control'
							id='startDate'
							name='startDate'
							onChange={handleChange}
							type='date'
							value={currentLeague.startDate || ""}
						/>
					</div>
					<div className='col-12 col-md-4'>
						<label className='form-label' htmlFor='endDate'>
							Fecha fin
						</label>
						<input
							className='form-control'
							id='endDate'
							name='endDate'
							onChange={handleChange}
							type='date'
							value={currentLeague.endDate || ""}
						/>
					</div>
					<div className='col-12 col-md-4'>
						<label className='form-label' htmlFor='championTeamId'>
							ChampionTeamId
						</label>
						<input
							className='form-control'
							id='championTeamId'
							name='championTeamId'
							onChange={handleChange}
							type='number'
							value={currentLeague.championTeamId}
						/>
					</div>
					<div className='col-12 col-md-4'>
						<label className='form-label' htmlFor='subChampionTeamId'>
							SubChampionTeamId
						</label>
						<input
							className='form-control'
							id='subChampionTeamId'
							name='subChampionTeamId'
							onChange={handleChange}
							type='number'
							value={currentLeague.subChampionTeamId}
						/>
					</div>
					<div className='col-12 col-md-4 d-flex align-items-end'>
						<div className='form-check form-switch'>
							<input
								checked={Boolean(currentLeague.importFixtures)}
								className='form-check-input'
								id='importFixtures'
								name='importFixtures'
								onChange={handleChange}
								type='checkbox'
							/>
							<label className='form-check-label' htmlFor='importFixtures'>
								ImportFixtures
							</label>
						</div>
					</div>
					<div className='col-12 col-md-4 d-flex align-items-end'>
						<div className='form-check form-switch'>
							<input
								checked={Boolean(currentLeague.importFixtureResults)}
								className='form-check-input'
								id='importFixtureResults'
								name='importFixtureResults'
								onChange={handleChange}
								type='checkbox'
							/>
							<label
								className='form-check-label'
								htmlFor='importFixtureResults'
							>
								ImportFixtureResults
							</label>
						</div>
					</div>
					<div className='col-12 col-md-4 d-flex align-items-end'>
						<div className='form-check form-switch'>
							<input
								checked={Boolean(currentLeague.importFixtureEvents)}
								className='form-check-input'
								id='importFixtureEvents'
								name='importFixtureEvents'
								onChange={handleChange}
								type='checkbox'
							/>
							<label
								className='form-check-label'
								htmlFor='importFixtureEvents'
							>
								ImportFixtureEvents
							</label>
						</div>
					</div>
					<div className='col-12 col-md-4'>
						<label className='form-label' htmlFor='fixturesImportedOn'>
							FixturesImportedOn
						</label>
						<input
							className='form-control'
							disabled
							id='fixturesImportedOn'
							readOnly
							type='text'
							value={formatDateTime(currentLeague.fixturesImportedOn)}
						/>
					</div>
					<div className='col-12 col-md-4'>
						<label className='form-label' htmlFor='fixtureResultsImportedOn'>
							FixtureResultsImportedOn
						</label>
						<input
							className='form-control'
							disabled
							id='fixtureResultsImportedOn'
							readOnly
							type='text'
							value={formatDateTime(currentLeague.fixtureResultsImportedOn)}
						/>
					</div>
					<div className='col-12 col-md-4'>
						<label className='form-label' htmlFor='fixtureEventsImportedOn'>
							FixtureEventsImportedOn
						</label>
						<input
							className='form-control'
							disabled
							id='fixtureEventsImportedOn'
							readOnly
							type='text'
							value={formatDateTime(currentLeague.fixtureEventsImportedOn)}
						/>
					</div>
				</div>
				<div className='text-center mt-4'>
					<button className='btn btn-primary' disabled={isSaving} type='submit'>
						{isSaving ? "Guardando..." : "Guardar"}
					</button>
				</div>
			</form>
		</div>
	);
}

export default EditLeague;